import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Save, TestTube, Eye, EyeOff, Check, ExternalLink, ToggleLeft, ToggleRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import NavigationMenu from '../components/NavigationMenu';
import DynamicFooter from '../components/DynamicFooter';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCaptcha = () => {
  const { toast } = useToast();
  const [settings, setSettings] = useState({
    enabled: false,
    provider: 'recaptcha_v2',
    site_key: '',
    secret_key: '',
    protect_contact: true,
    protect_support: true,
    protect_register: false
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showSecret, setShowSecret] = useState(false);
  const [siteKeyPreview, setSiteKeyPreview] = useState('');
  const [secretKeyPreview, setSecretKeyPreview] = useState('');

  const token = localStorage.getItem('sendbox_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/settings/captcha`, { headers });
      const d = res.data;
      setSettings(prev => ({
        ...prev,
        enabled: d.enabled || false,
        provider: d.provider || 'recaptcha_v2',
        protect_contact: d.protect_contact !== false,
        protect_support: d.protect_support !== false,
        protect_register: d.protect_register || false
      }));
      setSiteKeyPreview(d.site_key_preview || '');
      setSecretKeyPreview(d.secret_key_preview || '');
    } catch (e) {
      console.error('Error loading captcha settings:', e);
    }
    setLoading(false);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const payload = { ...settings };
      if (!payload.site_key) delete payload.site_key;
      if (!payload.secret_key) delete payload.secret_key;
      await axios.post(`${API}/admin/settings/captcha`, payload, { headers });
      toast({ title: 'Configuration CAPTCHA enregistrée' });
      setSettings(prev => ({ ...prev, site_key: '', secret_key: '' }));
      loadSettings();
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Impossible de sauvegarder', variant: 'destructive' });
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full" />
      </div>
    );
  }

  const providers = [
    { id: 'recaptcha_v2', name: 'Google reCAPTCHA v2', desc: 'Checkbox "Je ne suis pas un robot"', url: 'https://www.google.com/recaptcha/admin' },
    { id: 'recaptcha_v3', name: 'Google reCAPTCHA v3', desc: 'Invisible, basé sur un score', url: 'https://www.google.com/recaptcha/admin' },
    { id: 'hcaptcha', name: 'hCaptcha', desc: 'Alternative respectueuse de la vie privée', url: 'https://dashboard.hcaptcha.com' }
  ];

  const selectedProvider = providers.find(p => p.id === settings.provider) || providers[0];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
      <NavigationMenu />
      <Toaster />
      <div className="max-w-3xl mx-auto px-6 pt-28 pb-20">
        <Link to="/admin/dashboard?tab=settings" className="inline-flex items-center gap-2 text-blue-300/60 hover:text-blue-300 mb-8 transition-colors" data-testid="back-to-settings">
          <ArrowLeft className="w-4 h-4" /> Retour à la configuration
        </Link>

        <div className="flex items-center gap-4 mb-8">
          <div className="p-3 bg-emerald-500/20 rounded-xl">
            <ShieldCheck className="w-8 h-8 text-emerald-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Configuration CAPTCHA</h1>
            <p className="text-blue-300/60">Protéger les formulaires contre le spam</p>
          </div>
          {settings.enabled && siteKeyPreview && (
            <span className="ml-auto text-xs bg-green-500/20 text-green-400 px-3 py-1.5 rounded-full flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5" /> Actif
            </span>
          )}
        </div>

        <div className="space-y-6">
          {/* Enable/Disable */}
          <div className="bg-slate-800/50 border border-blue-500/20 rounded-2xl p-5">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-white">Activer le CAPTCHA</h2>
                <p className="text-sm text-blue-300/50">Afficher un CAPTCHA sur les formulaires publics</p>
              </div>
              <button
                onClick={() => setSettings({ ...settings, enabled: !settings.enabled })}
                className="transition-colors"
                data-testid="captcha-toggle"
              >
                {settings.enabled
                  ? <ToggleRight className="w-12 h-12 text-emerald-400" />
                  : <ToggleLeft className="w-12 h-12 text-blue-500/30" />}
              </button>
            </div>
          </div>

          {settings.enabled && (
            <>
              {/* Provider Selection */}
              <div className="bg-slate-800/50 border border-blue-500/20 rounded-2xl overflow-hidden">
                <div className="p-5 border-b border-blue-500/20">
                  <h2 className="text-lg font-semibold text-white">Fournisseur CAPTCHA</h2>
                </div>
                <div className="p-5 space-y-3">
                  {providers.map(p => (
                    <button
                      key={p.id}
                      onClick={() => setSettings({ ...settings, provider: p.id })}
                      className={`w-full text-left rounded-xl p-4 transition-all ${
                        settings.provider === p.id
                          ? 'bg-emerald-500/10 border-2 border-emerald-500/40'
                          : 'bg-slate-900/30 border border-blue-500/10 hover:border-blue-500/30'
                      }`}
                      data-testid={`captcha-provider-${p.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-white font-medium">{p.name}</span>
                          <p className="text-sm text-blue-300/50 mt-0.5">{p.desc}</p>
                        </div>
                        {settings.provider === p.id && <Check className="w-5 h-5 text-emerald-400" />}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* API Keys */}
              <div className="bg-slate-800/50 border border-blue-500/20 rounded-2xl overflow-hidden">
                <div className="p-5 border-b border-blue-500/20">
                  <h2 className="text-lg font-semibold text-white">Clés API</h2>
                  <p className="text-sm text-blue-300/50 mt-1">
                    Obtenez vos clés sur{' '}
                    <a href={selectedProvider.url} target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:underline inline-flex items-center gap-1">
                      {selectedProvider.url.replace('https://', '')} <ExternalLink className="w-3 h-3" />
                    </a>
                  </p>
                </div>
                <div className="p-5 space-y-4">
                  <div>
                    <label className="block text-sm text-blue-300 mb-2">Clé du site (Site Key) *</label>
                    <input
                      type="text"
                      value={settings.site_key}
                      onChange={e => setSettings({ ...settings, site_key: e.target.value })}
                      placeholder={siteKeyPreview || 'Votre clé de site...'}
                      className="w-full bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                      data-testid="captcha-site-key-input"
                    />
                    <p className="text-xs text-blue-300/40 mt-1">Laissez vide pour garder la clé actuelle</p>
                  </div>
                  <div>
                    <label className="block text-sm text-blue-300 mb-2">Clé secrète (Secret Key) *</label>
                    <div className="relative">
                      <input
                        type={showSecret ? 'text' : 'password'}
                        value={settings.secret_key}
                        onChange={e => setSettings({ ...settings, secret_key: e.target.value })}
                        placeholder={secretKeyPreview || 'Votre clé secrète...'}
                        className="w-full bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40 pr-10"
                        data-testid="captcha-secret-key-input"
                      />
                      <button onClick={() => setShowSecret(!showSecret)} className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-400/50 hover:text-blue-300">
                        {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Protected Pages */}
              <div className="bg-slate-800/50 border border-blue-500/20 rounded-2xl overflow-hidden">
                <div className="p-5 border-b border-blue-500/20">
                  <h2 className="text-lg font-semibold text-white">Pages protégées</h2>
                  <p className="text-sm text-blue-300/50">Choisissez les formulaires à protéger</p>
                </div>
                <div className="p-5 space-y-3">
                  {[
                    { key: 'protect_contact', label: 'Page Contact', desc: 'Formulaire de contact public' },
                    { key: 'protect_support', label: 'Page Support', desc: 'Formulaire de création de ticket' },
                    { key: 'protect_register', label: 'Page Inscription', desc: 'Formulaire de création de compte' }
                  ].map(item => (
                    <div key={item.key} className="flex items-center justify-between p-3 bg-slate-900/30 rounded-xl">
                      <div>
                        <span className="text-white font-medium text-sm">{item.label}</span>
                        <p className="text-xs text-blue-300/40">{item.desc}</p>
                      </div>
                      <button
                        onClick={() => setSettings({ ...settings, [item.key]: !settings[item.key] })}
                        data-testid={`captcha-${item.key}-toggle`}
                      >
                        {settings[item.key]
                          ? <ToggleRight className="w-10 h-10 text-emerald-400" />
                          : <ToggleLeft className="w-10 h-10 text-blue-500/30" />}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Actions */}
          <div className="flex items-center gap-3">
            <Button
              onClick={saveSettings}
              disabled={saving}
              className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white px-6 py-5"
              data-testid="save-captcha-btn"
            >
              {saving ? 'Enregistrement...' : <><Save className="w-4 h-4 mr-2" /> Enregistrer</>}
            </Button>
          </div>
        </div>
      </div>
      <DynamicFooter fixed={true} />
    </div>
  );
};

export default AdminCaptcha;
